/*
 * Signing in and out, and the bell (SessionController, Admin/Tenant
 * AuthController, NotificationController). Prototype mode keeps its own
 * made-up session in stores/session.js; these answers agree with it, so a
 * sign-in, sign-out or profile check never fails.
 */
import { company } from "@/mock/masters";
import { ok, fail, invalid, notFound, hoursAgo, portal } from "./util";

const branding = { code: company.code, name: company.name, logo_url: null, primary_color: null, secondary_color: null };

const adminSession = () => ({
  user: { id: 1, name: "Platform Admin", email: "demo@deecrm360.test" },
  roles: ["system-admin"],
  permissions: [],
  has_all_permissions: true,
});

const tenantSession = () => ({
  user: { id: 1, name: "Jane Wanjiku", email: "demo@deecrm360.test", phone: "+254 722 410 118", job_title: "Sales Manager", status: "active" },
  roles: ["admin"],
  permissions: [],
  has_all_permissions: true,
  tenant_name: company.name,
  company: branding,
});

const N = (id, type, title, body, url, icon, tone, hours, read) => ({
  id, type, title, body, url, icon, tone, meta: null, read, emailed: false, at: hoursAgo(hours),
});

const notices = [
  N(1, "follow_up_overdue", "Follow-up overdue", "Call Lakeview SACCO was due yesterday.", "/prospects/4", "iconoir-clock", "warning", 1, false),
  N(2, "prospect_assigned", "Prospect assigned to you", "Kilimo Fresh Exporters (PRS-0101).", "/prospects/1", "iconoir-user-plus", "info", 24, false),
  N(3, "quotation_approval", "Quotation awaiting your approval", "QUO-2026-0142 · 18% discount for Savannah Hotels Group.", "/quotations", "iconoir-check-circle", "primary", 30, false),
  N(4, "renewal_due", "Renewal in 30 days", "Savannah Hotels Group – HR & Payroll Standard.", "/renewals", "iconoir-refresh-double", "info", 48, true),
  N(5, "ticket_breach", "SLA about to breach", "TKT-0401 (Harambee Sacco) has 2 hours left to first response.", "/support", "iconoir-warning-triangle", "danger", 52, true),
  N(6, "role_change_applied", "Role change applied", "Ann Mutua now holds Sales Manager.", "/system-administration/role-assignments", "iconoir-shield-check", "success", 96, true),
];

const resetSent = "If that email address has an account here, we have sent a password reset link to it. The link works once, for the next 60 minutes.";
const resetLink = (body) =>
  !body.token || !body.email || body.token === "expired"
    ? fail("This reset link is not valid or has expired. Ask for a new one from the sign-in page.", { code: "RESET_LINK_INVALID" }, 422)
    : null;

const unread = () => notices.filter((n) => !n.read).length;
let wantsEmail = true;

export default [
  // Whose address this is and who is signed in at it.
  ["get", "auth/session", () => {
    const scope = portal();
    return scope === "admin"
      ? ok({ scope, address: globalThis.location?.host ?? null, signed_in: true, session: adminSession() })
      : ok({ scope, address: globalThis.location?.host ?? null, company: branding, signed_in: true, session: tenantSession() });
  }],

  ["post", "admin/auth/login", () => ok(adminSession(), "Login successful")],
  ["post", "tenant/auth/login", () => ok(tenantSession(), "Login successful")],
  ["get", "admin/auth/profile", () => ok(adminSession())],
  ["get", "tenant/auth/profile", () => ok(tenantSession())],
  ["post", "admin/auth/logout", () => ok(null, "Logged out")],
  ["post", "tenant/auth/logout", () => ok(null, "Logged out")],
  ["get", "admin/auth/heartbeat", () => ok({ authenticated: true }, "OK")],
  ["get", "tenant/auth/heartbeat", () => ok({ authenticated: true }, "OK")],
  ["post", "tenant/invite/accept", () => ok(null, "Account activated. You can now log in.")],
  ["post", "admin/invite/accept", () => ok(null, "Account activated. You can now log in.")],

  // Forgotten passwords. Prototype mode sends nothing: any link with a token and an email works, except one whose token is "expired".
  ...["tenant", "admin"].flatMap((side) => [
    ["post", `${side}/auth/password/forgot`, ({ body }) =>
      body.email ? ok({ expires_in_minutes: 60 }, resetSent) : invalid({ email: ["Enter your email address."] })],
    ["post", `${side}/auth/password/reset/check`, ({ body }) => resetLink(body) ?? ok({ email: body.email, name: "Jane Wanjiku" }, "Choose a new password.")],
    ["post", `${side}/auth/password/reset`, ({ body }) => {
      const refused = resetLink(body);
      if (refused) return refused;
      if (!body.password || body.password !== body.password_confirmation) return invalid({ password: ["The passwords do not match."] }, "Choose a different password.");
      if (body.password.length < 10) return invalid({ password: ["It must be at least 10 characters long."] }, "Choose a different password.");
      return ok({ email: body.email }, "Your password has been changed and you have been signed out everywhere. Sign in with your new password.");
    }],
  ]),

  // The bell: your own notices only.
  ["get", "tenant/notifications", ({ query }) => {
    const rows = query.unread ? notices.filter((n) => !n.read) : notices;
    const perPage = Number(query.per_page) || 20;
    return ok({
      notifications: {
        current_page: 1, data: rows.slice(0, perPage), from: rows.length ? 1 : null, last_page: 1,
        per_page: perPage, to: Math.min(rows.length, perPage), total: rows.length,
      },
      unread: unread(),
      email: { company_sends: true, wanted: wantsEmail, address: "jane.wanjiku@demulla.co.ke" },
    });
  }],
  ["post", "tenant/notifications/read-all", () => {
    const count = unread();
    notices.forEach((n) => (n.read = true));
    return ok({ unread: 0 }, count === 1 ? "One notice marked as read." : `${count} notices marked as read.`);
  }],
  ["post", "tenant/notifications/:id/read", ({ params }) => {
    const notice = notices.find((n) => String(n.id) === params.id);
    if (!notice) return notFound("That notice is no longer there.");
    notice.read = true;
    return ok({ notification: notice, unread: unread() });
  }],
  ["put", "tenant/notifications/preferences", ({ body }) => {
    wantsEmail = !!body.wants_email_notices;
    return ok(
      { wanted: wantsEmail },
      wantsEmail ? "You will get these by email as well." : "You will only see these in the app from now on.",
    );
  }],
];
